import { Button, Classes, Label, Position } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { ItemSelector } from 'vet-bones/components';
import { isTesting } from 'vet-bones/utils';
import { SelectItem } from 'vet-bones/utils/select';

import { PropertyTypeSelector } from 'src/components/PropertyTypeSelector';
import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import { SelectableLinks } from 'src/components/SelectableLinks';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_CLASS_TYPE,
  PROJECT_DIAGRAM_ENTITY_DRAWER_DATATYPES_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectLinkTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { AttributeDrawerSensitivePropertyGroups } from 'src/containers/ProjectDiagram/components/attributeDrawer/AttributeDrawerSensitivePropertyGroups';
import { ProjectDiagramEditableIri } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableIri';
import { ProjectDiagramEditableTextArea } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableTextArea';
import { useAddModelToIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useAddModelToIri';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { useCreateModelConceptClassFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptClassFromQuery';
import {
  useAddClassToAttribute,
  useRemoveClassFromAttribute,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptClassForAttribute';
import { useModelSelectItems } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelSelectItems';
import { useUpdateAttribute } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateAttribute';
import { useUpdateDescriptionForIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateDescriptionForIri';
import { useUpdateIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateIri';
import { useUpdateLabel } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateLabel';
import { useUpdatePropertyType } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdatePropertyType';
import { useRemoveModelConceptAttribute } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelConceptAttribute';
import { useRemoveModelForIri } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelForIri';
import { attributeDraftChangesSelector } from 'src/selectors/draftAttribute';
import { entityDrawerAttributeDataSelector } from 'src/selectors/entityDrawerAttributeData';
import { projectAttributeDomainsByIriSelector } from 'src/selectors/projectAttributeDomainsByIri';
import { projectAttributeRangesByIriSelector } from 'src/selectors/projectAttributeRangesByIri';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { projectDatatypeItemsSelector } from 'src/selectors/projectDatatypeItems';
import { projectDescriptionsByIriSelector } from 'src/selectors/projectDescriptionsByIri';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectModelItemsSelector } from 'src/selectors/projectModelItems';
import { propertyTypeByIriSelector } from 'src/selectors/propertyType';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramAttributeDrawer: React.VFC = () => {
  const { projectId } = useRouterParams();

  const attributeData = useReduxSelector(
    entityDrawerAttributeDataSelector(projectId)
  );

  const attributeIri = attributeData?.value || '';

  const allModelItems = useReduxSelector(projectModelItemsSelector(projectId));

  const { isCustomPropertyType, propertyType } = useReduxSelector(
    propertyTypeByIriSelector(projectId, attributeIri)
  );

  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));

  const descriptionsByIri = useReduxSelector(
    projectDescriptionsByIriSelector(projectId)
  );

  const ranges = useReduxSelector(
    projectAttributeRangesByIriSelector(projectId)
  );

  const domains = useReduxSelector(
    projectAttributeDomainsByIriSelector(projectId)
  );

  const {
    addedDomains,
    isDatatypeModified,
    isDescriptionModified,
    isLabelModified,
  } = useReduxSelector(attributeDraftChangesSelector(projectId, attributeIri));

  const { availableModels, currentModels } = useModelSelectItems(attributeIri);

  const handleUpdateIri = useUpdateIri(attributeIri);

  const handleUpdateLabel = useUpdateLabel(attributeIri);

  const handleUpdateDescription = useUpdateDescriptionForIri(attributeIri);

  const handleRemoveModel = useRemoveModelForIri(attributeIri);

  const handleAddModel = useAddModelToIri(attributeIri);

  const handleUpdatePropertyType = useUpdatePropertyType(attributeIri);

  const handleClickClass = useClickModelConceptClassLink();

  const createNewClassFromQuery = useCreateModelConceptClassFromQuery();

  const handleRemoveAttribute = useRemoveModelConceptAttribute();

  // For Data Type Selector
  const datatypeItems = useReduxSelector(
    projectDatatypeItemsSelector(projectId)
  );
  const currentDatatypes = ranges[attributeIri] || [];

  const handleUpdateAttribute = useUpdateAttribute();

  const handleSelectDatatype = (selectedDatatypes: SelectItem[]) => {
    handleUpdateAttribute({
      attributeIri,
      datatypes: selectedDatatypes.map(({ value }) => value),
    });
  };

  // For Association Selector
  const allClassItems = useReduxSelector(projectClassItemsSelector(projectId));

  const currentAssociations = allClassItems.filter((item) =>
    domains[attributeIri]?.some((domain) => item.value === domain.value)
  );
  const availableAssociations = allClassItems.filter(
    (item) =>
      !domains[attributeIri]?.some((domain) => item.value === domain.value)
  );

  const handleAddAttribute = useAddClassToAttribute(attributeIri);
  const removeAttributeFromClass = useRemoveClassFromAttribute(attributeIri);

  const handleRemoveAssociation = (item: SelectItem) => {
    removeAttributeFromClass(item.value);
  };

  if (!attributeData || !attributeIri) {
    return null;
  }

  const label = labelsByIri[attributeIri];

  const descriptionForIri = descriptionsByIri[attributeData.value] || '';

  return (
    <>
      <div className={Classes.DRAWER_BODY}>
        {/* Attribute Name */}
        <ProjectDiagramEditableIri
          header={copy.components.projectDiagram.attributeDrawer.name}
          iri={attributeIri}
          isHighlighted={isLabelModified}
          label={label}
          onSetIri={handleUpdateIri}
          onSetLabel={handleUpdateLabel}
          placeholder={
            label === ''
              ? copy.components.projectDiagram.modelConceptAttribute.new
              : ''
          }
          testId={TEST_IDS.projectDiagram.entityDrawer.name}
        />
        <ProjectDiagramEditableTextArea
          header={copy.components.projectDiagram.entityDrawer.description}
          isHighlighted={isDescriptionModified}
          onSetValue={handleUpdateDescription}
          value={descriptionForIri}
        />

        {allModelItems.length > 1 ? (
          <SelectableLinks
            allowEmpty={false}
            buttonText=""
            filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
            header={copy.components.projectDiagram.entityDrawer.models}
            items={availableModels}
            itemTestId={(value, idx) =>
              getProjectDiagramEntityDrawerSelectLinkTestId(
                value,
                'models',
                idx
              )
            }
            onRemove={handleRemoveModel}
            onSelect={handleAddModel}
            removeDisabledTooltip={
              copy.components.projectDiagram.entityDrawer.modelsRemoveDisabled
            }
            selected={currentModels}
            selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
            selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
              'models'
            )}
          />
        ) : null}

        {/* Property Type */}
        {isCustomPropertyType ? (
          <PropertyTypeSelector
            debounced={!isTesting()}
            propertyType={propertyType}
            setPropertyType={handleUpdatePropertyType}
          />
        ) : null}

        {/* Data Type Selector */}
        <Label>
          {copy.components.projectDiagram.attributeDrawer.dataType}
          <div className="sd-drawer-selector-menu">
            <ItemSelector.Multi
              buttonClassName={classNames('sd-draft-highlight-candidate', {
                'sd-draft-highlight-candidate--highlighted': isDatatypeModified,
              })}
              buttonText={currentDatatypes
                .map((datatype) => datatype.text)
                .join(', ')}
              filterText={copy.components.defaults.filter}
              items={datatypeItems}
              itemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
              onSelect={handleSelectDatatype}
              popoverProps={{
                position: Position.BOTTOM_LEFT,
              }}
              resetOnClose
              selected={currentDatatypes}
              testId={getProjectDiagramEntityDrawerSelectorTestId(
                PROJECT_DIAGRAM_ENTITY_DRAWER_DATATYPES_TYPE
              )}
            />
          </div>
        </Label>

        {/* Associations - Classes*/}
        <SelectableLinks
          buttonText=""
          creatable
          createNewItemFromQuery={createNewClassFromQuery}
          createNewItemRenderer={createNewSelectItemRenderer}
          createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
            ''
          )}
          filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
          header={copy.components.projectDiagram.attributeDrawer.associations}
          highlightedValues={addedDomains}
          items={availableAssociations}
          itemTestId={(value, idx) =>
            getProjectDiagramEntityDrawerSelectLinkTestId(
              value,
              PROJECT_DIAGRAM_ENTITY_DRAWER_CLASS_TYPE,
              idx
            )
          }
          onClick={handleClickClass}
          onRemove={handleRemoveAssociation}
          onSelect={handleAddAttribute}
          selected={currentAssociations}
          selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
            PROJECT_DIAGRAM_ENTITY_DRAWER_CLASS_TYPE
          )}
        />
        <AttributeDrawerSensitivePropertyGroups />
      </div>

      <div className={Classes.DRAWER_FOOTER}>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectDiagram.entityDrawer.removeButton}
            onClick={() => handleRemoveAttribute({ attributeIri })}
            outlined
          >
            {copy.components.projectDiagram.entityDrawerButtons.remove}
          </Button>
        </div>
      </div>
    </>
  );
};
